import type { Attributes, Span, Tracer } from '@opentelemetry/api'
import opentelemetry from '@opentelemetry/api'
import type { ReactNode } from 'react'
import { Component } from 'react'

export interface ProfilerProps {
  children: ReactNode
  name: string
}

/**
 * The ProfilerWrapper class is a React component that creates OpenTelemetry spans for measuring the performance of component mount, update, and render.
 * @remarks
 * This component requires OpenTelemetry tracing instrumentation to be enabled in order to function properly.
 */
export class ProfilerWrapper extends Component<ProfilerProps> {
  protected mountSpan: Span | undefined = undefined
  protected mountSpanEndTime: number | undefined = undefined
  protected updateSpan: Span | undefined = undefined

  private get isOtelInitialized(): boolean {
    return !!opentelemetry
  }

  private get tracer(): Tracer {
    return opentelemetry?.trace.getTracer('@msdk/charybui', 'X.X.X')
  }

  /**
   * This function creates a span with a given name and optional start and end times and attributes, and
   * sets it as the active span.
   * @param {string} spanName - A string representing the name of the span to be created.
   * @param [options] - An optional object that can contain the following properties:
   * @returns A `Span` object is being returned.
   */
  private createSpan(
    spanName: string,
    options?: { startTime?: number; endTime?: number; attributes?: Attributes },
  ): Span {
    const span = this.tracer.startSpan(spanName, {
      startTime: options?.startTime,
      attributes: {
        'react.component.name': this.props.name,
        ...(options?.attributes ?? {}),
      },
    })

    opentelemetry?.trace.setSpan(opentelemetry.context.active(), span)

    if (options?.endTime) {
      span.end(options.endTime)
    }

    return span
  }

  /**
   * This function creates a child span with a given name and options, using the parent span provided.
   * @param {string} spanName - A string representing the name of the child span to be created.
   * @param {Span} parent - The `parent` parameter is a `Span` object that represents the parent span of
   * the child span being created. The child span will be a part of the trace that the parent span
   * belongs to.
   * @param [options] - The `options` parameter is an optional object that can contain the following
   * properties:
   * @returns The `createChildSpan` function is returning a `Span` object.
   */
  private createChildSpan(
    spanName: string,
    parent: Span,
    options?: { startTime?: number; endTime?: number; attributes?: Attributes },
  ): Span {
    let span: Span

    opentelemetry?.context.with(opentelemetry.trace.setSpan(opentelemetry.context.active(), parent), () => {
      span = this.createSpan(spanName, options)
    })
    // eslint-disable-next-line
    return span!
  }

  constructor(props: ProfilerProps) {
    super(props)

    if (this.isOtelInitialized) {
      this.mountSpan = this.createSpan('componentMount')
    } else {
      console.error(
        'React Profiler requires tracing instrumentation. Please enable it in the "instrumentations" section of your config.',
      )
    }
  }

  /**
   * This function ends a span if the component has mounted and the OpenTelemetry library has been
   * initialized.
   */
  override componentDidMount(): void {
    if (this.isOtelInitialized && this.mountSpan) {
      this.mountSpanEndTime = Date.now()
      this.mountSpan.end(this.mountSpanEndTime)
    }
  }

  /**
   * This function creates a child span for component update in a TypeScript React application.
   * @returns a boolean value, which is `true`.
   */
  override shouldComponentUpdate(): boolean {
    // if (this.isOtelInitialized && this.mountSpan && updateProps !== this.props.updateProps) {
    //   const changedProps = Object.keys(updateProps).filter((key) => updateProps[key] !== this.props.updateProps[key])
    //   if (changedProps.length > 0) {
    this.updateSpan = this.createChildSpan('componentUpdate', this.mountSpan as Span, {
      // attributes: {
      //   'react.component.changed_props': changedProps,
      // },
    })
    return true
  }

  /**
   * This function ends a span and sets it to undefined if the component has been updated and
   * initialized.
   */
  override componentDidUpdate(): void {
    if (this.isOtelInitialized && this.updateSpan) {
      this.updateSpan.end()
      this.updateSpan = undefined
    }
  }

  /**
   * This function creates a child span for component rendering if the Otel is initialized and the mount
   * span exists.
   */
  override componentWillUnmount(): void {
    if (this.isOtelInitialized && this.mountSpan) {
      this.createChildSpan('componentRender', this.mountSpan, {
        startTime: this.mountSpanEndTime,
        endTime: Date.now(),
      })
    }
  }

  /**
   * This is a TypeScript React function that overrides the render method to return the children of the
   * component.
   * @returns The `render()` method is being overridden and it returns the `children` prop passed to the
   * component.
   */
  override render(): ReactNode {
    return this.props.children
  }
}
